import React from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { 
  LayoutDashboard, 
  PenTool, 
  History, 
  BarChart2, 
  Settings, 
  User, 
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  isMobile: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  setCollapsed,
  isMobile,
  mobileOpen,
  onMobileClose
}) => {
  const isCompact = collapsed && !isMobile;
  const navItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/' },
    { label: 'Practice', icon: PenTool, path: '/practice' },
    { label: 'History', icon: History, path: '/history' },
    { label: 'Analytics', icon: BarChart2, path: '/analytics' },
    { label: 'Profile', icon: User, path: '/profile' },
    { label: 'Settings', icon: Settings, path: '/settings' },
  ];

  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 border-r bg-background transition-all duration-300 ease-in-out",
        isCompact ? "w-[80px]" : "w-[280px]",
        isMobile ? (mobileOpen ? "translate-x-0 shadow-xl" : "-translate-x-full") : "translate-x-0"
      )}
      style={{
        zIndex: 'var(--z-mobile-sidebar)',
        height: 'var(--app-viewport-height, 100vh)',
      }}
      aria-hidden={isMobile && !mobileOpen}
      aria-label="Primary navigation sidebar"
      data-testid="mobile-sidebar"
    >
      <div className="flex h-[calc(4rem+env(safe-area-inset-top))] items-center justify-between border-b px-4 pt-[env(safe-area-inset-top)]">
        <div className={cn("flex items-center gap-2 overflow-hidden", isCompact && "justify-center")}>
           <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold">
            IM
           </div>
           {!isCompact && (
             <span className="font-bold text-lg whitespace-nowrap">My IELTS AI</span>
           )}
        </div>
        {!isCompact && !isMobile && (
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8" 
            onClick={() => setCollapsed(true)}
            aria-label="Collapse sidebar"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div
        className="flex flex-col justify-between py-4"
        style={{ height: 'calc(var(--app-viewport-height, 100vh) - 4rem - env(safe-area-inset-top))' }}
      >
        <nav className="space-y-2 px-2" aria-label="Primary navigation">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all hover:bg-accent hover:text-accent-foreground",
                isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground",
                isCompact && "justify-center px-2"
              )}
              title={isCompact ? item.label : undefined}
              onClick={() => {
                if (isMobile) {
                  onMobileClose();
                }
              }}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {!isCompact && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {isCompact && (
          <div className="px-2 flex justify-center pb-4">
             <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setCollapsed(false)}
              aria-label="Expand sidebar"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </aside>
  );
};
